import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import { FiAlertTriangle, FiArrowRight, FiCalendar, FiCheckCircle, FiChevronDown, FiChevronRight, FiClock, FiDownload, FiEdit, FiExternalLink, FiEye, FiFileText, FiFilter, FiFlag, FiLoader, FiMail, FiMessageSquare, FiMic, FiMinus, FiPhone, FiPhoneIncoming, FiPlay, FiSearch, FiShare2, FiStar, FiClipboard, FiTarget, FiThumbsDown, FiThumbsUp, FiTrendingDown, FiTrendingUp, FiUser } from 'react-icons/fi';


const ActivityTimeline = ({ contactId, activities = [] }) => {
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Enhanced sample activities with AI call data
  const sampleActivities = [
    {
      id: '1',
      type: 'stage_change',
      title: 'Stage Updated: Interested',
      description: 'Moved from Lead to Interested based on AI call outcome',
      timestamp: '2024-01-22T14:35:00Z',
      user: 'System (AI Recommendation)',
      details: {
        fromStage: 'lead',
        toStage: 'interested',
        reason: 'AI call analysis indicated high interest level and engagement. Prospect asked detailed questions about compensation and requested follow-up meeting.',
        nextAction: 'Schedule overview meeting with regional manager',
        automatedChange: true,
        aiConfidence: 0.89
      },
      outcome: 'positive',
      priority: 'high'
    },
    {
      id: '2',
      type: 'call_outbound_ai',
      title: 'AI Call Completed - Sarah (Initial Outreach)',
      description: 'Successful AI outreach call with positive outcome',
      timestamp: '2024-01-22T14:20:00Z',
      user: 'Sarah AI Agent',
      details: {
        duration: '4:45',
        callType: 'ai_outreach',
        disposition: 'interested',
        outcome: 'interested',
        agentName: 'Sarah - Initial Outreach',
        agentVoice: 'professional',
        aiSummary: 'Contact answered and showed genuine interest in the opportunity. Asked detailed questions about compensation structure and growth potential. Expressed eagerness to learn more and agreed to follow-up meeting.',
        transcript: `AI: Hi Sebastian, this is Sarah calling from Prosperity Leaders regarding career opportunities for experienced insurance professionals in your area.

Sebastian: Oh, okay. What kind of opportunities are you talking about?

AI: We work with top-performing agents who are looking to significantly increase their earning potential while maintaining their client relationships. Based on your background in insurance, I think you'd be very interested in what we offer.

Sebastian: I'm definitely open to learning more. I've been looking for ways to grow my income. What kind of increases are we talking about?

AI: Many of our agents see 40-60% increases in their first year, with our top performers earning well into the six figures. We provide comprehensive training and support systems.

Sebastian: That sounds very promising. How would I learn more about this?

AI: I'd love to have one of our regional managers give you a detailed overview of our compensation structure and support systems. Would you be available for a brief call this week?

Sebastian: Yes, I think that would be very helpful. I'm definitely interested in exploring this further.

AI: Perfect! I'll have John Smith reach out to you within the next day to schedule something convenient. Thanks for your time, Sebastian.

Sebastian: Thank you, I look forward to hearing from him.`,
        sentiment: 'positive',
        interestLevel: 'high',
        recordingUrl: 'https://thoughtly.co/recordings/abc123',
        transcriptUrl: 'https://thoughtly.co/transcripts/abc123',
        aiConfidence: 0.89,
        keyPoints: [
          'Expressed strong interest in income growth',
          'Asked specific questions about compensation',
          'Agreed to follow-up meeting',
          'Professional and engaged throughout call'
        ],
        objections: [],
        nextSteps: [
          'Schedule overview meeting with regional manager',
          'Send company overview materials',
          'Prepare compensation presentation'
        ],
        callCost: '$0.24',
        thoughtlyCallId: 'th_call_abc123'
      },
      outcome: 'interested',
      priority: 'high'
    },
    {
      id: '3',
      type: 'contact_created',
      title: 'Contact Added to CRM',
      description: 'Imported from LinkedIn prospecting campaign',
      timestamp: '2024-01-15T10:30:00Z',
      user: 'System',
      details: {
        source: 'linkedin_import',
        importBatch: 'Jan_2024_Dallas_Agents',
        initialStage: 'lead',
        leadScore: 85,
        matchCriteria: ['3+ years experience', 'Texas location', 'Active LinkedIn'],
        dataQuality: 'high'
      },
      outcome: 'neutral',
      priority: 'low'
    }
  ];

  const allActivities = activities.length > 0 ? activities : sampleActivities;

  // Activity type configurations with AI call support
  const activityConfig = {
    call_outbound: {
      icon: FiPhone,
      color: 'bg-blue-500',
      label: 'Manual Call',
      bgColor: 'bg-blue-50 border-blue-200'
    },
    call_outbound_ai: {
      icon: FiMic,
      color: 'bg-gradient-to-r from-purple-500 to-blue-500',
      label: 'AI Call',
      bgColor: 'bg-purple-50 border-purple-200',
      isAI: true
    },
    call_inbound: {
      icon: FiPhoneIncoming,
      color: 'bg-green-500',
      label: 'Inbound Call',
      bgColor: 'bg-green-50 border-green-200'
    },
    email_sent: {
      icon: FiMail,
      color: 'bg-indigo-500',
      label: 'Email Sent',
      bgColor: 'bg-indigo-50 border-indigo-200'
    },
    note_added: {
      icon: FiClipboard,
      color: 'bg-yellow-500',
      label: 'Note Added',
      bgColor: 'bg-yellow-50 border-yellow-200'
    },
    stage_change: {
      icon: FiTrendingUp,
      color: 'bg-pink-500',
      label: 'Stage Change',
      bgColor: 'bg-pink-50 border-pink-200'
    },
    contact_created: {
      icon: FiUser,
      color: 'bg-gray-500',
      label: 'Contact Created',
      bgColor: 'bg-gray-50 border-gray-200'
    }
  };

  // Enhanced outcome configurations
  const outcomeConfig = {
    positive: { color: 'text-green-600', bg: 'bg-green-100', icon: FiThumbsUp, label: 'Positive' },
    interested: { color: 'text-green-600', bg: 'bg-green-100', icon: FiStar, label: 'Interested' },
    neutral: { color: 'text-gray-600', bg: 'bg-gray-100', icon: FiMinus, label: 'Neutral' },
    negative: { color: 'text-red-600', bg: 'bg-red-100', icon: FiThumbsDown, label: 'Negative' },
    callback_requested: { color: 'text-blue-600', bg: 'bg-blue-100', icon: FiPhone, label: 'Callback Requested' },
    not_interested: { color: 'text-red-600', bg: 'bg-red-100', icon: FiTrendingDown, label: 'Not Interested' },
    in_progress: { color: 'text-orange-600', bg: 'bg-orange-100', icon: FiLoader, label: 'In Progress' }
  };

  // Filter activities
  const filteredActivities = useMemo(() => {
    return allActivities.filter(activity => {
      // Type filter
      if (filterType !== 'all' && activity.type !== filterType) return false;

      // Date range filter
      if (dateRange !== 'all') {
        const activityDate = new Date(activity.timestamp);
        const now = new Date();
        const daysAgo = Math.floor((now - activityDate) / (1000 * 60 * 60 * 24));

        switch (dateRange) {
          case '7d':
            if (daysAgo > 7) return false;
            break;
          case '30d':
            if (daysAgo > 30) return false;
            break;
          case '90d':
            if (daysAgo > 90) return false;
            break;
        }
      }

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          activity.title.toLowerCase().includes(searchLower) ||
          activity.description.toLowerCase().includes(searchLower) ||
          (activity.details?.aiSummary && activity.details.aiSummary.toLowerCase().includes(searchLower)) ||
          (activity.details?.content && activity.details.content.toLowerCase().includes(searchLower))
        );
      }

      return true;
    });
  }, [allActivities, filterType, dateRange, searchTerm]);

  const toggleExpanded = (activityId) => {
    setExpandedActivity(expandedActivity === activityId ? null : activityId);
  };

  const getRelativeTime = (timestamp) => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  const renderAICallDetails = (activity) => {
    const { details } = activity;
    
    return (
      <div className="space-y-4">
        {/* AI Call Header */}
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiMic} className="text-white text-sm" />
              </div>
              <div>
                <h5 className="font-medium text-purple-900">{details.agentName}</h5>
                <p className="text-sm text-purple-700 capitalize">{details.agentVoice} voice</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-purple-900">AI Confidence</p>
              <p className="text-lg font-bold text-purple-600">{Math.round(details.aiConfidence * 100)}%</p>
            </div>
          </div>
        </div>

        {/* Call Metrics */}
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <SafeIcon icon={FiClock} className="text-gray-400 mx-auto mb-1" />
            <p className="font-medium text-gray-900">{details.duration}</p>
            <p className="text-xs text-gray-500">Duration</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <SafeIcon icon={FiStar} className="text-yellow-500 mx-auto mb-1" />
            <p className="font-medium text-gray-900 capitalize">{details.interestLevel}</p>
            <p className="text-xs text-gray-500">Interest Level</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <SafeIcon icon={FiTrendingUp} className="text-green-500 mx-auto mb-1" />
            <p className="font-medium text-gray-900 capitalize">{details.sentiment}</p>
            <p className="text-xs text-gray-500">Sentiment</p>
          </div>
        </div>

        {/* AI Summary */}
        {details.aiSummary && (
          <div>
            <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <SafeIcon icon={FiMic} className="text-purple-600" />
              AI Summary
            </h5>
            <p className="text-sm text-gray-700 bg-purple-50 p-3 rounded-lg border border-purple-200">
              {details.aiSummary}
            </p>
          </div>
        )}

        {/* Key Points */}
        {details.keyPoints && details.keyPoints.length > 0 && (
          <div>
            <h5 className="font-medium text-gray-900 mb-2">Key Points</h5>
            <ul className="text-sm text-gray-700 space-y-1">
              {details.keyPoints.map((point, index) => (
                <li key={index} className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-0.5 flex-shrink-0 text-xs" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Next Steps */}
        {details.nextSteps && details.nextSteps.length > 0 && (
          <div>
            <h5 className="font-medium text-gray-900 mb-2">Recommended Next Steps</h5>
            <ul className="text-sm text-gray-700 space-y-1">
              {details.nextSteps.map((step, index) => (
                <li key={index} className="flex items-start gap-2">
                  <SafeIcon icon={FiArrowRight} className="text-blue-500 mt-0.5 flex-shrink-0 text-xs" />
                  {step}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Call Transcript */}
        {details.transcript && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h5 className="font-medium text-gray-900">Call Transcript</h5>
              <div className="flex gap-2">
                {details.recordingUrl && (
                  <a
                    href={details.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm"
                  >
                    <SafeIcon icon={FiPlay} className="text-xs" />
                    Play Recording
                  </a>
                )}
                {details.transcriptUrl && (
                  <a
                    href={details.transcriptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm"
                  >
                    <SafeIcon icon={FiDownload} className="text-xs" />
                    Download
                  </a>
                )}
              </div>
            </div>
            <div className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto whitespace-pre-line border">
              {details.transcript}
            </div>
          </div>
        )}

        {/* Call Metadata */}
        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
          <div className="grid grid-cols-2 gap-4">
            {details.callCost && (
              <div>
                <span className="font-medium">Call Cost:</span>
                <span className="ml-2">{details.callCost}</span>
              </div>
            )}
            {details.thoughtlyCallId && (
              <div>
                <span className="font-medium">Call ID:</span>
                <span className="ml-2 font-mono">{details.thoughtlyCallId}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderActivityDetails = (activity) => {
    const { details, type } = activity;
    if (!details) return null;

    // Handle AI calls with special rendering
    if (type === 'call_outbound_ai') {
      return renderAICallDetails(activity);
    }

    // Handle other activity types
    switch (type) {
      case 'call_outbound':
      case 'call_inbound':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Duration:</span>
                <span className="ml-2 font-medium">{details.duration}</span>
              </div>
              <div>
                <span className="text-gray-500">Disposition:</span>
                <span className="ml-2 font-medium capitalize">{details.disposition}</span>
              </div>
            </div>
            {details.summary && (
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Call Summary</h5>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{details.summary}</p>
              </div>
            )}
          </div>
        );

      case 'stage_change':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium capitalize">
                  {details.fromStage.replace('_', ' ')}
                </span>
                <SafeIcon icon={FiArrowRight} className="text-gray-400" />
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium capitalize">
                  {details.toStage.replace('_', ' ')}
                </span>
              </div>
              {details.automatedChange && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                  AI Recommended
                </span>
              )}
            </div>
            {details.reason && (
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Reason for Change</h5>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{details.reason}</p>
              </div>
            )}
            {details.aiConfidence && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiMic} className="text-purple-600 text-sm" />
                  <span className="text-sm font-medium text-purple-900">AI Confidence</span>
                </div>
                <p className="text-lg font-bold text-purple-600">{Math.round(details.aiConfidence * 100)}%</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-sm text-gray-600">
            Additional details not available for this activity type.
          </div>
        );
    }
  };

  if (filteredActivities.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiClock} className="text-4xl text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Activities Found</h3>
        <p className="text-gray-600 mb-4">
          {searchTerm || filterType !== 'all' || dateRange !== 'all'
            ? 'No activities match your current filters.'
            : 'No activities have been recorded for this contact yet.'}
        </p>
        {(searchTerm || filterType !== 'all' || dateRange !== 'all') && (
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterType('all');
              setDateRange('all');
            }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear Filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            <SafeIcon icon={FiFilter} />
            Filters
            <SafeIcon icon={showFilters ? FiChevronDown : FiChevronRight} className="text-sm" />
          </button>
          <div className="flex gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity Type</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Types</option>
                    <option value="call_outbound_ai">AI Calls</option>
                    <option value="call_outbound">Manual Calls</option>
                    <option value="email_sent">Emails</option>
                    <option value="stage_change">Stage Changes</option>
                    <option value="note_added">Notes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Time</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setFilterType('all');
                      setDateRange('all');
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Activity Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div className="space-y-6">
          {filteredActivities.map((activity, index) => {
            const config = activityConfig[activity.type];
            const outcomeInfo = outcomeConfig[activity.outcome];
            const isExpanded = expandedActivity === activity.id;

            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="relative"
              >
                {/* Enhanced Timeline dot for AI calls */}
                <div className={`absolute left-4 w-4 h-4 rounded-full border-2 border-white shadow-sm flex items-center justify-center z-10 ${
                  config.isAI ? config.color : config.color
                }`}>
                  <SafeIcon icon={config.icon} className="text-white text-xs" />
                </div>

                {/* Activity card */}
                <div className={`ml-12 ${config.bgColor} border rounded-lg p-4 ${
                  config.isAI ? 'ring-1 ring-purple-200' : ''
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-gray-900">{activity.title}</h4>
                            {config.isAI && (
                              <span className="px-2 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs rounded-full font-medium">
                                Thoughtly AI
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{activity.description}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <SafeIcon icon={FiClock} />
                              {getRelativeTime(activity.timestamp)}
                            </span>
                            <span className="flex items-center gap-1">
                              <SafeIcon icon={FiUser} />
                              {activity.user}
                            </span>
                            {outcomeInfo && (
                              <span className={`flex items-center gap-1 px-2 py-1 rounded-full ${outcomeInfo.bg} ${outcomeInfo.color}`}>
                                <SafeIcon icon={outcomeInfo.icon} />
                                {outcomeInfo.label}
                              </span>
                            )}
                            {activity.details?.aiConfidence && (
                              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-600">
                                <SafeIcon icon={FiMic} />
                                {Math.round(activity.details.aiConfidence * 100)}% confidence
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {activity.details && (
                      <button
                        onClick={() => toggleExpanded(activity.id)}
                        className="ml-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <SafeIcon icon={isExpanded ? FiChevronDown : FiChevronRight} className="text-lg" />
                      </button>
                    )}
                  </div>

                  {/* Expanded details */}
                  <AnimatePresence>
                    {isExpanded && activity.details && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-gray-200"
                      >
                        {renderActivityDetails(activity)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Enhanced Summary stats */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-purple-600">
              {allActivities.filter(a => a.type === 'call_outbound_ai').length}
            </p>
            <p className="text-sm text-gray-600">AI Calls</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">
              {allActivities.filter(a => a.type.includes('call') && a.type !== 'call_outbound_ai').length}
            </p>
            <p className="text-sm text-gray-600">Manual Calls</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-indigo-600">
              {allActivities.filter(a => a.type.includes('email')).length}
            </p>
            <p className="text-sm text-gray-600">Emails</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">
              {allActivities.filter(a => a.outcome === 'positive' || a.outcome === 'interested').length}
            </p>
            <p className="text-sm text-gray-600">Positive Outcomes</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-pink-600">
              {allActivities.filter(a => a.type === 'stage_change').length}
            </p>
            <p className="text-sm text-gray-600">Stage Changes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityTimeline;